import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import type {
  FleetAircraftView,
  AircraftType,
  AirportView,
  FlightSchedule,
  FlightStatusSnapshot,
  NewFlightLeg,
  NewMaintenanceEvent,
  RouteAnalyticsView,
  RouteSeatShareView,
  SimClock,
  UICommand,
} from '../../domain/models/contracts';
import {
  MINUTES_PER_DAY,
  buildTimelineBlocks,
  ceilToSnap,
  clockToAbsMinute,
  computeCheckStatuses,
  computeFleetStatusCounts,
  computeFleetUtilization,
  dailyFlightHours,
  groupByAircraft,
  legLabel,
  type CheckStatus,
  type ScheduleContext,
  type TimelineBlock,
} from '../../domain/scheduling/scheduleMath';
import { AddFlightLegModal } from './schedule/AddFlightLegModal';
import { AircraftTrack } from './schedule/AircraftTrack';
import { FleetAnalyticsHeader } from './schedule/FleetAnalyticsHeader';
import { RouteDetailModal } from './analytics/RouteDetailModal';
import { routesFlownByAircraft } from './analytics/routeAggregates';
import { NowLine } from './schedule/NowLine';
import { ScheduleMaintenanceModal } from './schedule/ScheduleMaintenanceModal';
import { TimeAxis } from './schedule/TimeAxis';
import './FleetScheduleGrid.css';

/** A route handed over from another view, to open the add-leg dialog with the destination filled in. */
export interface PendingScheduleLeg {
  readonly registration: string;
  readonly destinationId: string;
}

export interface FleetScheduleGridProps {
  fleet: readonly FleetAircraftView[];
  aircraftTypes: Readonly<Record<string, AircraftType>>;
  airports: Readonly<Record<string, AirportView>>;
  schedule: FlightSchedule;
  /** Simulation clock with minute resolution (the worker's TICK_HOURLY only carries the hour). */
  clock: SimClock;
  /** Latest hourly snapshot from the worker. */
  activeFlights: readonly FlightStatusSnapshot[];
  /** Sends a command to the simulation Web Worker. */
  dispatch: (command: UICommand) => void;
  /** Opens the add-leg dialog for this aircraft and destination. Null or undefined means nothing is pending. */
  pendingLeg?: PendingScheduleLeg | null;
  /** Called once the pending leg has opened its dialog, so it is not reapplied. */
  onPendingLegHandled?: () => void;
  /** Per-route analytics; the routes an aircraft flies open in the shared route detail modal. */
  analytics: readonly RouteAnalyticsView[];
  competitorShares: readonly RouteSeatShareView[];
}

type ModalState =
  | { kind: 'NONE' }
  | { kind: 'ADD_LEG'; aircraftReg: string; departureMinute: number; destinationId: string | null }
  | { kind: 'MAINTENANCE'; aircraftReg: string; startAbsMinute: number }
  | { kind: 'ANALYTICS'; aircraftReg: string };

interface HoverState {
  reg: string;
  minute: number;
}

const EMPTY_BLOCKS: readonly TimelineBlock[] = [];
const EMPTY_STATUSES: readonly CheckStatus[] = [];

export function FleetScheduleGrid({
  fleet,
  aircraftTypes,
  airports,
  schedule,
  clock,
  activeFlights,
  dispatch,
  pendingLeg,
  onPendingLegHandled,
  analytics,
  competitorShares,
}: FleetScheduleGridProps): ReactElement {
  const [selectedReg, setSelectedReg] = useState<string | null>(null);
  const [selectedLegId, setSelectedLegId] = useState<string | null>(null);
  const [hover, setHover] = useState<HoverState | null>(null);
  const [modal, setModal] = useState<ModalState>({ kind: 'NONE' });

  const nowAbs = clockToAbsMinute(clock);
  // Maintenance projections only need hour-level freshness; keeps tracks from
  // recomputing on every clock tick.
  const projectionHour = Math.floor(nowAbs / 60);

  /* ---------------- derived data ---------------- */

  const legsByReg = useMemo(() => groupByAircraft(schedule.legs), [schedule.legs]);
  const eventsByReg = useMemo(() => groupByAircraft(schedule.maintenance), [schedule.maintenance]);

  const blocksByReg = useMemo(() => {
    const result = new Map<string, readonly TimelineBlock[]>();
    for (const aircraft of fleet) {
      const reg = aircraft.registration;
      result.set(
        reg,
        buildTimelineBlocks(
          reg,
          legsByReg.get(reg) ?? [],
          eventsByReg.get(reg) ?? [],
          aircraftTypes[aircraft.typeId]?.minTurnaroundMinutes ?? 0,
          clock.dayIndex,
        ),
      );
    }
    return result;
  }, [fleet, legsByReg, eventsByReg, aircraftTypes, clock.dayIndex]);

  const checkStatusesByReg = useMemo(() => {
    const result = new Map<string, readonly CheckStatus[]>();
    const at = projectionHour * 60;
    for (const aircraft of fleet) {
      const reg = aircraft.registration;
      result.set(
        reg,
        computeCheckStatuses(aircraft, legsByReg.get(reg) ?? [], eventsByReg.get(reg) ?? [], at),
      );
    }
    return result;
  }, [fleet, legsByReg, eventsByReg, projectionHour]);

  const dailyHoursByReg = useMemo(() => {
    const result = new Map<string, number>();
    for (const aircraft of fleet) {
      result.set(aircraft.registration, dailyFlightHours(legsByReg.get(aircraft.registration) ?? []));
    }
    return result;
  }, [fleet, legsByReg]);

  const utilization = useMemo(
    () => computeFleetUtilization(fleet, schedule, clock.dayIndex),
    [fleet, schedule, clock.dayIndex],
  );

  const statusCounts = useMemo(
    () => computeFleetStatusCounts(fleet, aircraftTypes, schedule, nowAbs, activeFlights),
    [fleet, aircraftTypes, schedule, nowAbs, activeFlights],
  );

  const context = useMemo<ScheduleContext>(
    () => ({ aircraftTypes, airports, fleet, schedule, nowAbsMinute: nowAbs }),
    [aircraftTypes, airports, fleet, schedule, nowAbs],
  );

  // A selection can go stale when the worker removes the aircraft or leg.
  const activeAircraft = useMemo(
    () => fleet.find((a) => a.registration === selectedReg) ?? null,
    [fleet, selectedReg],
  );
  const activeLeg = useMemo(
    () => schedule.legs.find((leg) => leg.id === selectedLegId) ?? null,
    [schedule.legs, selectedLegId],
  );

  /* ---------------- interactions ---------------- */

  const openAddLeg = useCallback(
    (reg: string, departureMinute: number, destinationId: string | null = null): void => {
      const aircraft = fleet.find((a) => a.registration === reg);
      if (aircraft === undefined || aircraftTypes[aircraft.typeId] === undefined) return;
      setModal({ kind: 'ADD_LEG', aircraftReg: reg, departureMinute, destinationId });
    },
    [fleet, aircraftTypes],
  );

  // A route from the market planner opens the add-leg dialog at the current clock minute.
  useEffect(() => {
    if (pendingLeg === null || pendingLeg === undefined) return;
    openAddLeg(pendingLeg.registration, ceilToSnap(clock.minuteOfDay), pendingLeg.destinationId);
    onPendingLegHandled?.();
  }, [pendingLeg, openAddLeg, onPendingLegHandled, clock.minuteOfDay]);

  const onSelectAircraft = useCallback((reg: string): void => {
    setSelectedReg(reg);
    setSelectedLegId(null);
  }, []);

  const onSelectBlock = useCallback((block: TimelineBlock): void => {
    setSelectedReg(block.aircraftReg);
    setSelectedLegId(block.legId);
  }, []);

  const onLaneClick = useCallback(
    (reg: string, minute: number): void => {
      setSelectedReg(reg);
      setSelectedLegId(null);
      setHover(null);
      openAddLeg(reg, minute);
    },
    [openAddLeg],
  );

  const onLaneHover = useCallback((reg: string, minute: number | null): void => {
    setHover((previous) => {
      if (minute === null) return previous !== null && previous.reg === reg ? null : previous;
      if (previous !== null && previous.reg === reg && previous.minute === minute) return previous;
      return { reg, minute };
    });
  }, []);

  const closeModal = useCallback((): void => setModal({ kind: 'NONE' }), []);

  const handleHeaderAddLeg = (): void => {
    if (activeAircraft === null) return;
    const next = Math.min(MINUTES_PER_DAY - 5, ceilToSnap(clock.minuteOfDay));
    openAddLeg(activeAircraft.registration, next);
  };

  const handleHeaderDelete = (): void => {
    if (activeLeg === null) return;
    dispatch({ type: 'DELETE_FLIGHT_LEG', legId: activeLeg.id });
    setSelectedLegId(null);
  };

  const handleHeaderMaintenance = (): void => {
    if (activeAircraft === null) return;
    setModal({
      kind: 'MAINTENANCE',
      aircraftReg: activeAircraft.registration,
      startAbsMinute: ceilToSnap(nowAbs),
    });
  };

  const handleHeaderAnalytics = (): void => {
    if (activeAircraft === null) return;
    setModal({ kind: 'ANALYTICS', aircraftReg: activeAircraft.registration });
  };

  const submitLeg = (leg: NewFlightLeg): void => {
    dispatch({ type: 'CREATE_FLIGHT_LEG', leg });
    closeModal();
  };

  const submitMaintenance = (event: NewMaintenanceEvent): void => {
    dispatch({ type: 'SCHEDULE_MAINTENANCE', event });
    closeModal();
  };

  /* ---------------- modal resolution ---------------- */

  const modalAircraft =
    modal.kind === 'NONE' ? undefined : fleet.find((a) => a.registration === modal.aircraftReg);
  const modalType = modalAircraft === undefined ? undefined : aircraftTypes[modalAircraft.typeId];

  return (
    <section className="fsg" aria-label="Fleet rotation schedule">
      <FleetAnalyticsHeader
        clock={clock}
        utilization={utilization}
        statusCounts={statusCounts}
        selectedAircraftReg={activeAircraft === null ? null : activeAircraft.registration}
        selectedLegLabel={activeLeg === null ? null : legLabel(activeLeg)}
        onAddLeg={handleHeaderAddLeg}
        onDeleteLeg={handleHeaderDelete}
        onScheduleMaintenance={handleHeaderMaintenance}
        onOpenAircraftAnalytics={handleHeaderAnalytics}
      />

      <div className="fsg-scroll">
        <div className="fsg-grid" role="grid" aria-label="24-hour fleet timeline">
          <TimeAxis minuteOfDay={clock.minuteOfDay} />
          <div className="fsg-body">
            {fleet.length === 0 && (
              <p className="fsg-empty fsg-empty-fleet">
                No aircraft in your fleet yet. Acquire an aircraft to start building a rotation.
              </p>
            )}
            {fleet.map((aircraft) => {
              const reg = aircraft.registration;
              return (
                <AircraftTrack
                  key={reg}
                  aircraft={aircraft}
                  type={aircraftTypes[aircraft.typeId] ?? null}
                  blocks={blocksByReg.get(reg) ?? EMPTY_BLOCKS}
                  checkStatuses={checkStatusesByReg.get(reg) ?? EMPTY_STATUSES}
                  dailyHours={dailyHoursByReg.get(reg) ?? 0}
                  selected={reg === selectedReg}
                  selectedLegId={selectedLegId}
                  hoverMinute={hover !== null && hover.reg === reg ? hover.minute : null}
                  onSelectAircraft={onSelectAircraft}
                  onSelectBlock={onSelectBlock}
                  onLaneClick={onLaneClick}
                  onLaneHover={onLaneHover}
                />
              );
            })}
            {fleet.length > 0 && (
              <div className="fsg-now-layer">
                <NowLine minuteOfDay={clock.minuteOfDay} />
              </div>
            )}
          </div>
        </div>
      </div>

      {modal.kind === 'ADD_LEG' && modalAircraft !== undefined && modalType !== undefined && (
        <AddFlightLegModal
          aircraft={modalAircraft}
          type={modalType}
          context={context}
          initialDepartureMinute={modal.departureMinute}
          initialDestinationId={modal.destinationId}
          onSubmit={submitLeg}
          onClose={closeModal}
        />
      )}
      {modal.kind === 'ANALYTICS' && modalAircraft !== undefined && (
        <RouteDetailModal
          title={`${modalAircraft.registration} routes`}
          subtitle="Routes flown by this aircraft on the most recent daily close"
          routes={routesFlownByAircraft(analytics, modalAircraft.registration)}
          competitorShares={competitorShares}
          onClose={closeModal}
        />
      )}
      {modal.kind === 'MAINTENANCE' && modalAircraft !== undefined && modalType !== undefined && (
        <ScheduleMaintenanceModal
          aircraft={modalAircraft}
          type={modalType}
          context={context}
          initialStartAbsMinute={modal.startAbsMinute}
          onSubmit={submitMaintenance}
          onClose={closeModal}
        />
      )}
    </section>
  );
}
